import {
  getBoundedValue,
  StrengthAssessmentManager,
  StrengthAssessmentStep,
  UserProfile,
  WizardManager,
} from 'fitbeat.gym-core';
import {
  SmallBodyGroupType,
  StrengthRatioForMuscleGroups,
} from 'fitbeat.models';
import { createSelector } from 'reselect';
import { IAppStore } from '../HomeWorkouts';
import theme from '../theme';
import { adjustHexByPerceptualScale } from '../utils/hexColourScaling';

export interface IStrengthResult {
  primaryMuscleMover: any;
  value: number;
  color: string;
}

export const svgFillPropertyToBodyGroup = {
  fill: 'Shoulders',
  fillSecondary: 'Triceps',
  color: 'Chest',
  colorSecondary: 'Abdomen',
  colorFour: 'Glutes & Hips',
  colorFive: 'Upper Legs',
  colorSix: 'Biceps',
  colorSeven: 'Upper Back & Traps',
  colorEight: 'Lower Back & Lats',
};

const getSteps = (state: IAppStore) => state.strengthAssessment.steps;
const getUserStrengthRatios = (state: IAppStore) =>
  state.strengthAssessment.userStrengthRatios;
const getStepsAreLoading = (state: IAppStore) =>
  state.strengthAssessment.stepsAreLoading;
const getAnswers = (state: IAppStore) => state.strengthAssessment.answers;
const getIsAssessmentResultLoading = (state: IAppStore) =>
  state.strengthAssessment.strengthAssessmentResultsLoading;

export const selectStepsAndAnswers = createSelector(
  [getSteps, getAnswers],
  (steps, answers) => {
    return {
      steps,
      answers,
    };
  },
);

export const selectAnswers = createSelector([getAnswers], (answers) => answers);

export const selectSteps = createSelector([getSteps], (steps) => {
  return steps;
});

export const selectStepsAreLoading = createSelector(
  [getStepsAreLoading],
  (steps) => {
    return steps;
  },
);

export const selectAnswer = createSelector(
  [getAnswers, (_, currentStep) => currentStep],
  (answers, currentStep) => {
    return answers[currentStep.questionId]?.userWeight;
  },
);

export const selectNextButtonDisabled = createSelector(
  [getAnswers, (_, currentStep) => currentStep],
  (answers, currentStep: StrengthAssessmentStep) => {
    return (
      !answers[currentStep.questionId] &&
      (currentStep.config.type === 'SelectWeight' || currentStep.config.type === 'SelectDumbbellWeight')
    );
  },
);

export const selectIsAssessmentResultLoading = createSelector(
  [getIsAssessmentResultLoading],
  (isLoading) => isLoading,
);

export const selectStrengthAssessmentStepProgress = createSelector(
  [selectStepsAndAnswers, (_, currentStep) => currentStep],
  (stepsAndAnswers, currentStep) => {
    const { steps } = stepsAndAnswers;
    if (!currentStep.config.shouldShowProgressBar) {
      return null;
    }
    const indexOfCurrentStep = WizardManager.getIndexOfCurrentStep({
      ...stepsAndAnswers,
      currentStep,
    });
    if (!indexOfCurrentStep) {
      return null;
    }
    const numProgressSteps = steps.filter((step) => step.config.shouldShowProgressBar).length;
    const firstProgressStep = steps.findIndex(((step) => step.config.shouldShowProgressBar)) + 1;
    const indexOfPrevStep = Math.max(indexOfCurrentStep - 1, 0);
    const to = (indexOfCurrentStep - firstProgressStep + 2 ) / numProgressSteps;
    const from = (indexOfPrevStep - firstProgressStep + 2) / numProgressSteps;
    return {
      from,
      to,
    };
  },
);

export const selectStrengthAssessmentResultsBeforeAssessment: (
  state: IAppStore,
  userProfile: UserProfile,
) => IStrengthResult[] = createSelector(
  [getUserStrengthRatios, (_, userProfile) => userProfile],
  (userStrengthRatios, userProfile) => {
    const values = Object.entries(userStrengthRatios).map(
      ([primaryMuscleMover, ratio]) => {
        return {
          primaryMuscleMover: primaryMuscleMover as SmallBodyGroupType,
          ratio,
        };
      },
    );
    return buildStrengthAssessmentResults(values, userProfile);
  },
);

export const selectStrengthAssessmentResultsAfterAssessment: (
  state: IAppStore,
  userProfile: UserProfile,
) => IStrengthResult[] = createSelector(
  [selectAnswers, (_, userProfile) => userProfile],
  (answers, userProfile) => {
    return buildStrengthAssessmentResults(Object.values(answers), userProfile);
  },
);

export const selectUserStrengthRatios: (
  state: IAppStore,
) => StrengthRatioForMuscleGroups = createSelector(
  [getUserStrengthRatios],
  (userStrengthRatios) => {
    return { ...userStrengthRatios } as StrengthRatioForMuscleGroups;
  },
);

const buildStrengthAssessmentResults = (
  strenghtInfo: {
    ratio: number;
    primaryMuscleMover: SmallBodyGroupType;
  }[],
  userProfile: UserProfile,
): IStrengthResult[] => {
  const answersSorted = strenghtInfo.sort((a, b) => a.ratio - b.ratio);

  const results = answersSorted.map(({ primaryMuscleMover, ratio }) => {
    const ratioToAbsoluteValueEquation = StrengthAssessmentManager.getPercentageForStrengthRatio(userProfile);
    const value = ratioToAbsoluteValueEquation(ratio);
    const roundedToTen = Math.round(value / 10) * 10;
    const boundedValue = getBoundedValue(roundedToTen, 10, 100);
    return {
      primaryMuscleMover,
      value,
      color: adjustHexByPerceptualScale(theme.PRIMARY, (boundedValue / 10 - 1)),
    };
  });
  return results;
};

export const selectIsFirstStep =  createSelector(
  [getSteps, (_, questionId) => questionId],
  (steps, questionId) => {
    return steps[0].questionId === questionId;
  },
);

export const selectIsLastStep =  createSelector(
  [getSteps, (_, questionId) => questionId],
  (steps, questionId) => {
    return steps[steps.length-1].questionId === questionId;
  },
);
